import React, { Component } from 'react';
import * as XLSX from 'xlsx';
import { connect } from 'react-redux';
import Api from '../../../../../../../lib/api';
import swal from 'sweetalert2';
import { dataImportConfig } from '../../template-configuration';
import SpinnerLoader from '../../../../../../common/loader/SpinnerLoader';
import { fetchPreviousImports, fetchCustomFieldsForImport } from '../../actions';

const normalizeHeader = (value) => (
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\(required\)\s*$/i, '')
        .replace(/[_\s-]+/g, '')
);

// Import types whose file header row must match the Holder's current template
// exactly — every template header present and no unrecognized columns. The
// remaining types keep the legacy required-columns-only check.
const STRICT_HEADER_TEMPLATE_TYPES = [2];

// Spreadsheet column letters for the rejected-rows CSV (A..Z, AA..).
const getColumnLetter = (columnIndex) => {
    let index = Number(columnIndex);
    if (Number.isNaN(index) || index < 0) return '';
    let letter = '';
    let remaining = index;
    do {
        letter = String.fromCharCode(65 + (remaining % 26)) + letter;
        remaining = Math.floor(remaining / 26) - 1;
    } while (remaining >= 0);
    return letter;
};

class ImportDataModal extends Component {
    state = {
        selected: '',
        selectedFile: null,
        isDragging: false,
        isImporting: false,
        fileErrors: [],
        fileHeaderErrors: [],
        validationErrors: [],
        warningRows: [],
        warningsCount: 0,
        uploadedHeaders: [],
        rawRows: [],
        validRows: [],
        failedRows: [],
        importLogs: [],
        isLoadingFile: false,
        customFields: [],
    };

    // Holder id → active custom fields, used to validate that a custom field
    // column is configured for the Holder referenced by an individual row.
    _holderCustomFieldsCache = {};

    componentDidMount() {
        this._isMounted = true;

        if (this.props.customFields && this.props.customFields.length > 0) {
            this.setState({ customFields: this.props.customFields });
            this.seedPageHolderCustomFields(this.props.customFields);
        }
    }

    componentDidUpdate(prevProps) {
        // Import happens in one step: the modal is opened after the custom fields
        // request has been dispatched, so sync them in and re-derive validation.
        const prevFields = prevProps.customFields || [];
        const nextFields = this.props.customFields || [];

        if (prevFields !== nextFields && nextFields.length > 0) {
            this.seedPageHolderCustomFields(nextFields);
            this.applyValidationState({ customFields: nextFields });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    /**
     * The page Holder's own fields are already known, so cache them instead of
     * requesting them again when rows reference the page Holder.
     */
    seedPageHolderCustomFields = (customFields) => {
        const holderId = String(this.props.holderID || '');
        if (!holderId || this._holderCustomFieldsCache[holderId] !== undefined) return;
        this._holderCustomFieldsCache[holderId] = customFields || [];
    };

    getSelectedTemplateFields = (selectedValue = this.state.selected, customFields = []) => {
        const typeId = parseInt(selectedValue, 10);
        if (!typeId) return [];
        return dataImportConfig.getTemplateFieldDefinitions(typeId, customFields);
    };

    getFieldDisplayLabel = (field) => (field && field.label ? field.label : '');

    getHeaderIndexByField = (headers, field) => (
        dataImportConfig.mapTemplateFieldsToHeaderIndexes([field], headers)[field.key]
    );

    isCustomFieldDefinition = (field) => !!(field && field.isCustomField);

    /**
     * Custom fields configured for a Holder, indexed by custom field name.
     * Used on parent holder pages to detect columns that are not configured for
     * the Holder referenced by an individual row.
     */
    getCustomFieldsByName = (customFields = []) => {
        const map = {};

        (customFields || []).forEach(customField => {
            const definition = dataImportConfig.buildCustomFieldDefinitions([customField])[0];
            if (definition && definition.label) {
                map[definition.label] = definition;
            }
        });

        return map;
    };

    /**
     * Ensures custom fields for every Holder referenced by the file are loaded.
     * Only needed on parent holder pages, where the page Holder's fields are not
     * necessarily the fields of the Holder in a given row.
     */
    ensureHolderCustomFieldsLoaded = async (holderIds = []) => {
        if (!holderIds || holderIds.length === 0) return;

        const pending = holderIds.filter(holderId => this._holderCustomFieldsCache[holderId] === undefined);
        if (pending.length === 0) return;

        // This modal defines mapDispatchToProps, so connect() does not inject the
        // raw `dispatch` prop. Use the bound action prop instead.
        const loadHolderCustomFields = this.props.loadHolderCustomFields;

        await Promise.all(pending.map(async holderId => {
            if (typeof loadHolderCustomFields !== 'function') {
                this._holderCustomFieldsCache[holderId] = [];
                return;
            }

            try {
                const fields = await loadHolderCustomFields(holderId);
                this._holderCustomFieldsCache[holderId] = fields || [];
            } catch (err) {
                // One failed lookup must not reject the whole validation run; rows
                // then fall back to the page Holder's custom field configuration.
                this._holderCustomFieldsCache[holderId] = [];
            }
        }));
    };

    /**
     * Full header check for the strict types: every template header has to be
     * present and no column may be unknown (renamed / archived custom fields).
     */
    validateHeaders = (headers, templateFields) => {
        const { missing, unrecognized } = dataImportConfig.compareHeaders(headers, templateFields);
        const errors = [];

        if (missing.length > 0) {
            errors.push(`Headers don’t match the template: [${missing.join(', ')}]`);
        }

        if (unrecognized.length > 0) {
            errors.push(`Headers not recognized: [${unrecognized.join(', ')}]`);
        }

        return errors;
    };

    // Only the strict types are checked here; the legacy types keep the required
    // column check inside validateCsvAgainstTemplate.
    getHeaderErrors = (typeId, headers, templateFields) => (
        STRICT_HEADER_TEMPLATE_TYPES.includes(typeId)
            ? this.validateHeaders(headers, templateFields)
            : []
    );

    validateCsvAgainstTemplate = (headers, rawRows, templateFields) => {
        const normalizedHeaders = headers.map(h =>
            String(h || '').trim().toLowerCase()
                .replace(/\(required\)$/i, '')
                .replace(/[_\s-]+/g, '')
        );

        const headerMap = {}; // field.key → column index
        const missingRequired = [];

        templateFields.forEach(field => {
            const normalizedLabel = String(field.label || '')
                .trim()
                .toLowerCase()
                .replace(/\(required\)$/i, '')
                .replace(/[_\s-]+/g, '');

            const idx = normalizedHeaders.findIndex(h => h === normalizedLabel);
            headerMap[field.key] = idx;

            if (field.required && idx === -1) {
                missingRequired.push(field.label);
            }
        });

        if (missingRequired.length > 0) {
            return {
                ok: false,
                error: `Headers don’t match the template: [${missingRequired.join(', ')}]`
            };
        }
        const holderField = templateFields.find(f =>
            normalizeHeader(f.label).includes('holderid') ||
            normalizeHeader(f.label).includes('hiringclient') ||
            normalizeHeader(f.label).includes('holder')
        );

        const isParentView = this.props.holderChildrens.length > 0;
        const currentHolderId = String(this.props.holderID || '');

        let allowedHolderIds = new Set([currentHolderId]);

        if (isParentView) {
            this.props.holderChildrens.forEach(c => {
                allowedHolderIds.add(String(c.Id));
            });
        }

        const failedRows = [];
        const validRows = [];
        const warningRows = [];

        rawRows.forEach((row, idx) => {
            // skip completely empty rows
            if (!row || !row.some(cell => String(cell || '').trim())) return;

            const rowErrors = [];
            const rowWarnings = [];
            const rowValues = [];

            const rowHolderId = holderField && headerMap[holderField.key] >= 0
                ? String((row && row[headerMap[holderField.key]]) || '').trim()
                : '';
            const shouldValidateHolderFields = isParentView && rowHolderId && allowedHolderIds.has(rowHolderId);

            const holderCustomFieldsByName = shouldValidateHolderFields
                ? (this._holderCustomFieldsCache[rowHolderId] !== undefined
                    ? this.getCustomFieldsByName(this._holderCustomFieldsCache[rowHolderId])
                    : null)
                : null;

            const holderCustomFieldsKnown = !shouldValidateHolderFields ||
                this._holderCustomFieldsCache[rowHolderId] !== undefined;

            templateFields.forEach(field => {
                const colIdx = headerMap[field.key];
                const value = colIdx >= 0 ? String(row[colIdx] || '').trim() : '';

                rowValues.push(value);

                if (this.isCustomFieldDefinition(field)) {
                    // A row whose Holder does not have this custom field configured
                    // is rejected, rather than silently dropping the value.
                    if (shouldValidateHolderFields && holderCustomFieldsKnown) {
                        const holderHasField = !!holderCustomFieldsByName[field.label];
                        if (!holderHasField) {
                            rowErrors.push(`${field.label}: not configured for Holder ${rowHolderId}`);
                            return;
                        }
                    }

                    // Fall back to the page Holder's definition when the field is
                    // not configured on the row's Holder and that cannot be proven.
                    const holderFieldDefinition = holderCustomFieldsByName && holderCustomFieldsByName[field.label];
                    const effectiveField = holderFieldDefinition || field;
                    const result = dataImportConfig.validateCustomFieldValue(effectiveField, value);

                    if (!result.ok) {
                        rowErrors.push(result.reason);
                    }

                    if (!value && !effectiveField.required) {
                        rowWarnings.push(`Custom field "${field.label}" is empty (optional)`);
                    }

                    return;
                }

                if (field.required && !value) {
                    rowErrors.push(`${field.label} is required`);
                } else if (value && !dataImportConfig.validateFieldValue(field, value)) {
                    rowErrors.push(`${field.label} has invalid format`);
                }

                if (!field.required && !value) {
                    if (field.key && String(field.key).startsWith('custom_')) {
                        rowWarnings.push(`Custom field "${field.label}" is empty (optional)`);
                    } else {
                        rowWarnings.push(`${field.label} is empty`);
                    }
                }

                if (holderField && field.key === holderField.key) {
                    if (!value) {
                        rowErrors.push("Missing Holder ID.");
                    } else if (!allowedHolderIds.has(value)) {
                        rowErrors.push("The import contains Holder IDs that are not associated with this account.");
                    }
                }
            });

            if (rowWarnings.length > 0) {
                warningRows.push({
                    rowNumber: idx + 2,
                    warnings: rowWarnings,
                    values: rowValues
                });
            }

            if (rowErrors.length > 0) {
                failedRows.push({
                    rowNumber: idx + 2,
                    errors: rowErrors,
                    values: rowValues
                });
            } else {
                validRows.push({
                    rowNumber: idx + 2,
                    values: rowValues
                });
            }
        });

        let validationErrors = [];
        if (failedRows.length > 0) {
            validationErrors = failedRows.slice(0, 6).map(r =>
                `Row ${r.rowNumber}: ${r.errors.join(', ')}`
            );
            if (failedRows.length > 6) {
                validationErrors.push(`... and ${failedRows.length - 6} more`);
            }
        }

        return {
            ok: true,
            hasRowLevelErrors: failedRows.length > 0,
            validationErrors,
            failedRows,
            validRows,
            headerMap,
            warningRows,
            warningsCount: warningRows.length
        };
    };

    deriveValidationState = (snapshot) => {
        const typeId = parseInt(snapshot.selected, 10);

        // Only the Projects template carries custom field columns; every other
        // type is validated against its standard schema, as before.
        const validationCustomFields = STRICT_HEADER_TEMPLATE_TYPES.includes(typeId)
            ? (snapshot.customFields || [])
            : [];

        const templateFields = this.getSelectedTemplateFields(snapshot.selected, validationCustomFields);

        if (!templateFields.length || !snapshot.rawRows.length) {
            return { fileHeaderErrors: [], validationErrors: [], failedRows: [], validRows: [], warningRows: [], warningsCount: 0 };
        }

        // File-level checks run before any row is processed. The file type and row
        // cap are enforced on attach; the header check happens here so all
        // problems are reported together in a single rejection.
        const headerErrors = this.getHeaderErrors(typeId, snapshot.uploadedHeaders, templateFields);

        if (headerErrors.length > 0) {
            return {
                fileHeaderErrors: headerErrors,
                validationErrors: [],
                failedRows: [],
                validRows: [],
                warningRows: [],
                warningsCount: 0
            };
        }

        const result = this.validateCsvAgainstTemplate(
            snapshot.uploadedHeaders,
            snapshot.rawRows,
            templateFields
        );

        if (!result.ok && result.error) {
            return {
                fileHeaderErrors: [result.error],
                validationErrors: [],
                failedRows: [],
                validRows: [],
                warningRows: [],
                warningsCount: 0
            };
        }

        const isHolderError = (err) =>
            err.includes('Missing Holder ID.') ||
            err.includes('The import contains Holder IDs that are not associated with this account');

        const holderErrors = result.failedRows.filter(row =>
            row.errors.some(isHolderError)
        );

        let fileHeaderErrors = [];
        let validationErrors = result.validationErrors;

        if (holderErrors.length > 0) {
            const errorMap = {};

            holderErrors.forEach(row => {
                row.errors
                    .filter(isHolderError)
                    .forEach(err => {
                        if (!errorMap[err]) errorMap[err] = [];
                        errorMap[err].push(row.rowNumber);
                    });
            });

            fileHeaderErrors = Object.entries(errorMap).map(([err, rows]) =>
                rows.length > 1
                    ? `Rows(${rows.join(', ')}): ${err}`
                    : `Row ${rows[0]}: ${err}`
            );

            validationErrors = result.validationErrors.filter(err => !isHolderError(err));
        }

        return {
            fileHeaderErrors: fileHeaderErrors,
            validationErrors: validationErrors,
            failedRows: result.failedRows,
            validRows: holderErrors.length > 0 ? [] : result.validRows,
            warningRows: result.warningRows,
            warningsCount: result.warningsCount,
            hasRowLevelErrors: result.hasRowLevelErrors
        };
    };

    applyValidationState = (overrides = {}, callback) => {
        const snapshot = { ...this.state, ...overrides };
        const derived = this.deriveValidationState(snapshot);

        this.setState({
            ...overrides,
            ...derived
        }, callback);
    };

    /**
     * Loads the custom fields of every Holder referenced by the file once the
     * header check has passed, then re-runs validation so rows can be rejected
     * with "<Field Name>: not configured for Holder <holder Id>".
     */
    runValidation = async (snapshot) => {
        if (!STRICT_HEADER_TEMPLATE_TYPES.includes(parseInt(snapshot.selected, 10))) return;

        const templateFields = this.getSelectedTemplateFields(snapshot.selected, snapshot.customFields || []);
        if (!templateFields.length || !snapshot.rawRows.length) return;

        if (this.validateHeaders(snapshot.uploadedHeaders, templateFields).length > 0) return;

        const headerMap = dataImportConfig.mapTemplateFieldsToHeaderIndexes(templateFields, snapshot.uploadedHeaders);
        const holderField = templateFields.find(f =>
            normalizeHeader(f.label).includes('holderid') ||
            normalizeHeader(f.label).includes('hiringclient') ||
            normalizeHeader(f.label).includes('holder')
        );

        if (!holderField || headerMap[holderField.key] < 0) return;

        const holderColumnIndex = headerMap[holderField.key];
        const holderIds = [...new Set(
            (snapshot.rawRows || [])
                .map(row => String((row && row[holderColumnIndex]) || '').trim())
                .filter(Boolean)
        )].filter(holderId => this._holderCustomFieldsCache[holderId] === undefined);

        if (holderIds.length === 0) return;

        if (this._isMounted) this.setState({ isLoadingFile: true });

        await this.ensureHolderCustomFieldsLoaded(holderIds);

        if (!this._isMounted) return;
        this.applyValidationState({}, () => this.setState({ isLoadingFile: false }));
    };

    handleTypeChange = (e) => {
        if (this.state.isImporting) return;

        const typeId = parseInt(e.target.value, 10);
        const fields = STRICT_HEADER_TEMPLATE_TYPES.includes(typeId)
            ? (this.props.customFields || [])
            : [];

        // Custom field columns come from the page Holder's configuration, so
        // re-apply them after switching template and drop cached child fields.
        this._holderCustomFieldsCache = {};
        this.seedPageHolderCustomFields(fields);

        this.applyValidationState({
            selected: e.target.value,
            fileErrors: [],
            customFields: fields
        }, () => {
            this.runValidation({ ...this.state }).catch(() => {
                if (this._isMounted) this.setState({ isLoadingFile: false });
            });
        });
    };

handleFileChange = (e) => {

    if (this.state.isImporting) {
        return;
    }

    let file = null;

    if (e.target.files.length) {
        file = e.target.files[0];
    } else if (e.dataTransfer.files.length) {
        file = e.dataTransfer.files[0];
    }

    if (!file) return;

    this.setState({ isLoadingFile: true }, () => {
        setTimeout(() => {

            const errors = [];

            const ext = file.name.toLowerCase().split('.').pop();
            if (ext !== 'csv') {
                this.setState({
                    fileErrors: ['Only csv files allowed'],
                    selectedFile: null,
                    uploadedHeaders: [],
                    rawRows: [],
                    fileHeaderErrors: [],
                    validationErrors: [],
                    validRows: [],
                    failedRows: [],
                    warningRows: [],
                    warningsCount: 0,
                    importLogs: [],
                    isLoadingFile: false
                });
                return;
            }

            const reader = new FileReader();

            reader.onload = (evt) => {
                try {
                    const data = evt.target.result;

                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];

                    const worksheet = workbook.Sheets[sheetName];
                    const json = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

                    const headers = (json[0] || []).map(h => String(h || '').trim());
                    const rows = json.slice(1);
                    const rowCount = rows.length;

                    if (rowCount > 500) {
                        errors.push(`Too many rows: ${rowCount} (max 500)`);
                    } else if (rowCount < 1) {
                        errors.push('File is empty (minimum 1 row required)');
                    }

                    if (!headers.some(Boolean)) {
                        errors.push('Header row is missing or invalid');
                    }

                    if (errors.length > 0) {

                        this.setState({
                            fileErrors: errors,
                            selectedFile: null,
                            uploadedHeaders: [],
                            rawRows: [],
                            fileHeaderErrors: [],
                            validationErrors: [],
                            validRows: [],
                            failedRows: [],
                            warningRows: [],
                            warningsCount: 0,
                            importLogs: [],
                            isLoadingFile: false
                        });
                        return;
                    }

                    this._holderCustomFieldsCache = {};

                    this.applyValidationState({
                        fileErrors: [],
                        selectedFile: file,
                        uploadedHeaders: headers,
                        rawRows: rows
                    }, () => {
                        this.setState({ isLoadingFile: false });
                        // Parent holder pages need the custom fields of every
                        // Holder referenced by the file before rows are judged.
                        this.runValidation({ ...this.state }).catch(() => {
                            if (this._isMounted) this.setState({ isLoadingFile: false });
                        });
                    });

                } catch (err) {

                    this.setState({
                        fileErrors: ['Failed to read file'],
                        selectedFile: null,
                        uploadedHeaders: [],
                        rawRows: [],
                        fileHeaderErrors: [],
                        validationErrors: [],
                        validRows: [],
                        failedRows: [],
                        warningRows: [],
                        warningsCount: 0,
                        importLogs: [],
                        isLoadingFile: false
                    });
                }
            };

            reader.onerror = (err) => {
                this.setState({ isLoadingFile: false });
            };

            reader.readAsArrayBuffer(file);

        }, 500);
    });
};

    handleImport = () => {
        const { selected, selectedFile, fileErrors, fileHeaderErrors, validRows, failedRows, customFields } = this.state;
        if (!selected || !selectedFile || fileErrors.length > 0 || fileHeaderErrors.length > 0 || validRows.length < 1) return;

        const { templateTypes, holderID, authToken, onImportStart, onImportStatusChange, currentUserName, customFields: reduxCustomFields } = this.props;
        const selectedType = templateTypes.find(type => type.id == selected);
        const importType = selectedType ? this.getImportTypeDisplayName(selectedType.name) : '';
        const importLogId = `local-import-${Date.now()}`;
        const nowIso = new Date().toISOString();

        // For Type 4 (Single Step PI Import), use custom fields from props or state
        const finalCustomFields = reduxCustomFields && reduxCustomFields.length > 0 ? reduxCustomFields : (customFields && customFields.length > 0 ? customFields : []);

        this.executeImport({
            importType, importLogId, nowIso, holderID, authToken, onImportStart, onImportStatusChange, currentUserName, selectedFile,
            customFields: finalCustomFields
        });
    };

    executeImport = ({ importType, importLogId, nowIso, holderID, authToken, onImportStart, onImportStatusChange, currentUserName, selectedFile, customFields }) => {
        const { selected, failedRows } = this.state;
        this.setState({ isImporting: true });
        const templateFields = this.getSelectedTemplateFields(selected, customFields);

        const headerIndexMap = {};
        const normalizedHeaders = this.state.uploadedHeaders.map(h =>
            String(h || '').trim().toLowerCase()
                .replace(/\(required\)$/i, '')
                .replace(/[_\s-]+/g, '')
        );

        templateFields.forEach(field => {
            const normalizedLabel = String(field.label || '')
                .trim()
                .toLowerCase()
                .replace(/\(required\)$/i, '')
                .replace(/[_\s-]+/g, '');
            const idx = normalizedHeaders.findIndex(h => h === normalizedLabel);
            headerIndexMap[field.key] = idx;
        });

        const customFieldHeaderMap = {};
        
        if (customFields && customFields.length > 0) {
            templateFields.forEach(field => {
                if (field.key && String(field.key).startsWith('custom_')) {
                    const customFieldId = field.key.replace('custom_', '');
                    const colIdx = headerIndexMap[field.key];
                    if (colIdx >= 0 && customFieldId) {
                        customFieldHeaderMap[customFieldId] = colIdx;
                    }
                }
            });
        }

        const formattedData = (this.state.rawRows || [])
            .filter(row => row && row.some(cell => String(cell || '').trim()))
            .map((row, index) => {
                const rowObject = {
                    rowNumber: index + 2
                };

                templateFields.forEach(field => {
                    if (field.key && String(field.key).startsWith('custom_')) {
                        return;
                    }
                    const colIdx = headerIndexMap[field.key];
                    const value = colIdx >= 0 ? String((row && row[colIdx]) || '').trim() : '';
                    rowObject[field.key] = value;
                    
                    if (field.key === 'InsuredEmail' && value &&
                        !dataImportConfig.validateFieldValue(field, value)) {
                        rowObject.__emailInvalid = true;
                    }
                });

                // Add custom fields for Type 4
                if (customFields && customFields.length > 0 && Object.keys(customFieldHeaderMap).length > 0) {
                    const customFieldValues = {};
                    Object.keys(customFieldHeaderMap).forEach(customFieldId => {
                        const colIdx = customFieldHeaderMap[customFieldId];
                        const value = String((row && row[colIdx]) || '').trim();
                        if (colIdx >= 0 && value) {
                            customFieldValues[customFieldId] = value;
                        }
                    });
                    if (Object.keys(customFieldValues).length > 0) {
                        rowObject.customFields = customFieldValues;
                    }
                }

                return rowObject;
            });

        if (typeof onImportStart === 'function') {
            onImportStart({
                id: importLogId,
                documentName: selectedFile.name,
                documentLink: '#',
                uploadedBy: currentUserName || '',
                importType,
                importTypeID: Number(selected),
                uploadDate: nowIso,
                recordCount: formattedData.length,
                status: 'In Progress',
                errorCount: (this.state.failedRows && this.state.failedRows.length) || 0,
                processedAt: ''
            });
        }

        const payload = {
            importType: importType.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-').trim(),
            documentName: selectedFile.name,
            data: formattedData,
            holderId: holderID,
            // VE-8464: the file's own header row, so the backend validates it against the
            // holder-aware template (missing + unrecognized lists) before any row is processed.
            headers: this.state.uploadedHeaders
        };

        Api.post('cf/fileDataImports', payload, authToken)
            .then(response => {
                const resp = response.data.data || {};
                const logFiles = resp.logFiles || [];
                const uploadedHeaders = this.state.uploadedHeaders;
                const selected = this.state.selected;

                // Extract counts from logFiles: type 'success' = accepted, type 'error' = rejected
                const successLog = logFiles.find(f => f.type === 'success');
                const errorLog = logFiles.find(f => f.type === 'error');
                const importLog = logFiles.find(f => f.type === 'import');
                
                var successCount = (successLog && successLog.recordCount) || resp.acceptedCount || 0;
                var rejectedCount = (errorLog && errorLog.recordCount) || resp.rejectedCount || 0;
                var totalCount = (importLog && importLog.recordCount) || successCount + rejectedCount || formattedData.length;
                const errorCount = rejectedCount || failedRows.length;

                const logItems = logFiles.map(f => ({
                    fileName: f.fileName,
                    downloadUrl: f.downloadUrl,
                    recordCount: f.recordCount,
                    Type: f.type === 'success' ? 'Accept' : f.type === 'error' ? 'Reject' : 'Import'
                }));

                this.setState({ importLogs: logItems }, () => {
                    const hasError = errorCount > 0;
                    const allFailed = successCount === 0 && errorCount > 0;

                    const title = allFailed ? 'Import Failed' : (hasError ? 'Import Partially Failed' : 'Import Successful');
                    const type = allFailed ? 'error' : (hasError ? 'warning' : 'success');

                    swal({
                        title,
                        html: `<div style="margin:10px; font-size:0.9rem; color:#2e2e2e; display:flex; gap:12px; align-items:center; justify-content:center; text-align:center; font-weight:500;">
                                <div>
                                    Total Rows on File: <strong style="font-weight:700;">${totalCount}</strong>
                                </div>
                                <div>||</div>
                                <div>
                                    Accepted Count: <strong style="font-weight:700; color:#28a445">${successCount}</strong>
                                </div>
                                <div>||</div>
                                <div>
                                    Rejected Count: <strong style="font-weight:700; color:#e74a3b">${rejectedCount}</strong>
                                </div>
                            </div>`,
                        type,
                        showConfirmButton: successCount > 0,
                        showCancelButton: true,
                        confirmButtonText: successCount > 0 ? 'Download Success Log' : undefined,
                        cancelButtonText: 'Close',
                        confirmButtonColor: '#28a745',
                        cancelButtonColor: '#6c757d',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        preConfirm: () => {
                            if (successLog) {
                                window.open(successLog.downloadUrl, '_blank');
                            }
                            return false;
                        },
                        onOpen: () => {
                            const container = document.querySelector('.swal2-container');
                            if (container) container.classList.add('swal2-import-data-modal-container');

                            const actions = swal.getActions();
                            if (!actions) return;

                            actions.className = 'swal2-actions-btn-container';

                            const oldBtn = actions.querySelector('.swal2-error-btn');
                            if (oldBtn) oldBtn.remove();

                            const confirmBtn = actions.querySelector('.swal2-confirm');
                            if (!confirmBtn) return;

                            if (errorCount > 0 && errorLog) {
                                const errorBtn = document.createElement('button');
                                errorBtn.textContent = 'Download Error Log';
                                errorBtn.className = 'swal2-styled swal2-error-btn';
                                errorBtn.type = 'button';

                                errorBtn.onclick = (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(errorLog.downloadUrl, '_blank');
                                };

                                confirmBtn.parentNode.insertBefore(errorBtn, confirmBtn.nextSibling);
                            }
                        }
                    }).then(result => {
                        if (result.dismiss === swal.DismissReason.cancel) {
                            this.props.onClose();
                        }
                    });

                    if (typeof onImportStatusChange === 'function') {
                        onImportStatusChange(importLogId, {
                            status: allFailed ? 'Failed' : (hasError ? 'Partially Completed' : 'Completed'),
                            processedAt: new Date().toISOString(),
                            errorCount
                        });
                    }
                });
            })
            .catch(() => {
                if (typeof onImportStatusChange === 'function') {
                    onImportStatusChange(importLogId, {
                        status: 'Failed',
                        processedAt: new Date().toISOString(),
                        errorCount: formattedData.length
                    });
                }
                swal({
                    type: 'error',
                    title: 'Import Failed',
                    text: 'An error occurred while importing the data.',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                });
            })
            .finally(() => {
                if (this._isMounted) {
                    this.setState({ isImporting: false });
                }
                this.props.reloadImportHistory(this.props.holderID);
                this.props.onClose();
            });
    };

    downloadCSV(lines, prefix) {
        const csv = lines.join('\n');
        const bomCsv = '\uFEFF' + csv;
        const blob = new Blob([bomCsv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const safePrefix = String(prefix || '').replace(/\.csv$/i, '');
        const timestamp = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);
        link.download = `${safePrefix}_${timestamp}.csv`;
        
        link.style.display = 'none';
        document.body.appendChild(link);
        
        link.click();
        
        setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 100);
    }

    escapeCsvValue(value) {
        const safeValue = value === null || value === undefined ? '' : value;
        return String(safeValue).replace(/"/g, '""');
    }

    getSuccessBaseRow(item) {
        return [item.RowNum || '', 'Created'];
    }

    // Insureds success
    downloadInsuredsSuccessCSV(rows = []) {
        const lines = [
            "Insured Id,Holder Id,Insured Name,Contact Name,Contact Phone,Contact Email,Vendor Number,Link"
        ];

        rows.forEach(item => {
            const insuredId = item.newInsuredID || '';
            const link = insuredId ? `${process.env.REACT_APP_FRONTEND_URL}/certfocus/insureds/${insuredId}` : '';

            const row = [
                `"${insuredId}"`,
                `"${(item.HolderId || '').replace(/"/g, '""')}"`,
                `"${(item.InsuredName || '').replace(/"/g, '""')}"`,
                `"${(item.ContactName || '').replace(/"/g, '""')}"`,
                `"${(item.ContactPhone || '').replace(/"/g, '""')}"`,
                `"${(item.ContactEmail || '').replace(/"/g, '""')}"`,
                `"${(item.VendorNumber || '')}"`,
                `"${link}"`
            ];

            lines.push(row.join(','));
        });

        this.downloadCSV(lines, `insured_success_${new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)}.csv`);
    }

    // Insureds error - Sorted Cells + Synced Reasons
    downloadInsuredsErrorCSV(rows = [], uploadedHeaders = this.state.uploadedHeaders) {
        const lines = [
            "Row(Reference to Previous File),Cells,Reasons,Holder ID,Insured Name,Legal Name,Address,Address Line 2,City,Postal Code,State,Country,Contact Name,Contact Phone,Contact Fax,Contact Email,Tax ID,Vendor Number,Note"
        ];

        const headerToLetter = {};
        const additionalColumnCount = 3;
        (uploadedHeaders || []).forEach((header, idx) => {
            const letter = String.fromCharCode(65 + additionalColumnCount + idx);
            const norm = normalizeHeader(header);

            if (norm.includes('holder') || norm.includes('hiringclient')) headerToLetter.HolderId = letter;
            if (norm.includes('insuredname')) headerToLetter.InsuredName = letter;
            if (norm.includes('legalname')) headerToLetter.LegalName = letter;
            if (norm.includes('address') && !norm.includes('2')) headerToLetter.Address = letter;
            if (norm.includes('address2') || norm.includes('address line 2')) headerToLetter.AddressLine2 = letter;
            if (norm.includes('city')) headerToLetter.City = letter;
            if (norm.includes('postal') || norm.includes('zip')) headerToLetter.PostalCode = letter;
            if (norm.includes('state')) headerToLetter.State = letter;
            if (norm.includes('country')) headerToLetter.Country = letter;
            if (norm.includes('contactname')) headerToLetter.ContactName = letter;
            if (norm.includes('contactphone')) headerToLetter.ContactPhone = letter;
            if (norm.includes('contactfax')) headerToLetter.ContactFax = letter;
            if (norm.includes('email') || norm.includes('insuredemail')) headerToLetter.ContactEmail = letter;
            if (norm.includes('tax') || norm.includes('taxid')) headerToLetter.TaxId = letter;
            if (norm.includes('vendor') || norm.includes('vendornumber')) headerToLetter.VendorNumber = letter;
            if (norm.includes('note')) headerToLetter.Note = letter;
        });

        const consolidated = this.consolidateErrors();
        const failedData = this.getFailedRowsData ? this.getFailedRowsData() : [];   // fallback
        let errorRows = [...consolidated, ...failedData];

        const seen = new Set();
        errorRows = errorRows.filter(item => {
            const key = String(item.RowNum || item.rowNumber || '');
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        }).sort((a, b) => (a.RowNum || a.rowNumber || 0) - (b.RowNum || b.rowNumber || 0));

        errorRows.forEach(item => {
            let reasons = [];
            try {
                if (item.RejectionReason) {
                    reasons = typeof item.RejectionReason === 'string' && item.RejectionReason.startsWith('[') 
                        ? JSON.parse(item.RejectionReason) 
                        : [item.RejectionReason];
                } else if (Array.isArray(item.errors)) {
                    reasons = item.errors;
                }
            } catch {
                reasons = [item.RejectionReason || 'Unknown error'];
            }

            const reasonToCellMap = reasons.map(r => {
                const norm = String(r || '').toLowerCase();
                let field = null;
                if (norm.includes('insured name') && norm.includes('required')) field = 'InsuredName';
                else if (norm.includes('contact phone') && norm.includes('required')) field = 'ContactPhone';
                else if (norm.includes('address') && norm.includes('required')) field = 'Address';
                else if (norm.includes('city') && norm.includes('required')) field = 'City';
                else if (norm.includes('postal') && norm.includes('required')) field = 'PostalCode';
                else if (norm.includes('contact name') && norm.includes('required')) field = 'ContactName';
                else if (norm.includes('contact email') && norm.includes('required')) field = 'ContactEmail';
                else if (norm.includes('tax id') && norm.includes('required')) field = 'TaxId';
                else if (norm.includes('duplicate') && norm.includes('tax')) field = 'TaxId';
                else if (norm.includes('duplicate') && norm.includes('insured')) field = 'InsuredName';

                const letter = field && headerToLetter[field] ? headerToLetter[field] : '';
                return letter;
            });

            const cellStr = [...new Set(reasonToCellMap.filter(Boolean))].sort().join(',');
            const reasonStr = reasons.join(', ');

            const row = [
                `"${item.RowNum || item.rowNumber || ''}"`,
                `"${cellStr}"`,
                `"${reasonStr.replace(/"/g, '""')}"`,
                `"${(item.HolderId || item.HolderID ||'').replace(/"/g, '""')}"`,
                `"${(item.InsuredName || '').replace(/"/g, '""')}"`,
                `"${(item.LegalName || item.CFLegalName ||'').replace(/"/g, '""')}"`,
                `"${(item.Address || '').replace(/"/g, '""')}"`,
                `"${(item.AddressLine2 || item.Address2 || '').replace(/"/g, '""')}"`,
                `"${(item.City || '').replace(/"/g, '""')}"`,
                `"${(item.PostalCode || '').replace(/"/g, '""')}"`,
                `"${(item.State || '').replace(/"/g, '""')}"`,
                `"${(item.Country || item.CountryName || '').replace(/"/g, '""')}"`,
                `"${(item.ContactName || '').replace(/"/g, '""')}"`,
                `"${(item.ContactPhone || '').replace(/"/g, '""')}"`,
                `"${(item.ContactFax || '').replace(/"/g, '""')}"`,
                `"${(item.ContactEmail || item.InsuredEmail ||'').replace(/"/g, '""')}"`,
                `"${(item.TaxId || item.TaxID || '').replace(/"/g, '""')}"`,
                `"${(item.VendorNumber || '').replace(/"/g, '""')}"`,
                `"${(item.Note || '').replace(/"/g, '""')}"`
            ];

            lines.push(row.join(','));
        });

        this.downloadCSV(lines, `insured_error_log${new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)}.csv`);
    }

        
    // Projects success
    downloadProjectsSuccessCSV(rows = []) {
        const lines = [
            "Project Id,Holder Id,Project Name,Requirement Set IDs,Link"
        ];

        rows.forEach(item => {
            const projectId = item.newProjectId || '';
            const link = projectId ? `${process.env.REACT_APP_FRONTEND_URL}/certfocus/projects/${projectId}` : '';

            const row = [
                `"${projectId}"`,
                `"${this.escapeCsvValue(item.HolderId)}"`,
                `"${this.escapeCsvValue(item.ProjectName)}"`,
                `"${this.escapeCsvValue(item.RequirementSets)}"`,
                `"${link}"`
            ];
            lines.push(row.join(','));
        });

        this.downloadCSV(lines, `project_success_log_${new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)}.csv`);

    }

    // Projects error
    downloadProjectsErrorCSV(rows = [], uploadedHeaders = this.state.uploadedHeaders) {
        const lines = [
            "Row(Reference to Previous File),Cells,Reasons,Holder ID,Project Name,Optional Name,Account Manager,Address,Address Line 2,City,State,Country,Postal Code,Requirement Set IDs,Project Note,AI Wording,Contact Name,Contact Phone,Email Address,Project Number,Project Description"
        ];

        const headerLetterByNormalized = {};
        (uploadedHeaders || []).forEach((header, idx) => {
            const letter = getColumnLetter(3 + idx);
            const norm = normalizeHeader(header);
            if (norm) headerLetterByNormalized[norm] = letter;
        });

        const consolidatedErrors = this.consolidateErrors();
        let errorRows = consolidatedErrors.length > 0 ? consolidatedErrors : rows;

        const seen = new Set();
        errorRows = errorRows.filter(item => {
            const key = String(item.RowNum || item.rowNumber || '');
            if (seen.has(key)) return false;

            seen.add(key);
            return true;
        }).sort((a, b) => (a.RowNum || a.rowNumber || 0) - (b.RowNum || b.rowNumber || 0));

        // Custom field columns are part of the template, so their labels are
        // resolvable to a spreadsheet column for the rejected rows report.
        const templateFields = dataImportConfig.getTemplateFieldDefinitions(
            2,
            this.props.customFields || this.state.customFields || []
        );
        const labelByKey = templateFields.reduce((acc, field) => {
            acc[field.key] = field.label;
            return acc;
        }, {});

        const getLetterByLabel = (label) => {
            if (!label) return '';
            return headerLetterByNormalized[normalizeHeader(label)] || '';
        };

        const getLetterByKey = (key) => getLetterByLabel(labelByKey[key] || key);

        errorRows.forEach(item => {
            let reasons = [];
            let invalidCells = [];

            const rowNum = item.RowNum || item.rowNumber;

            try {
                let reasonsArray = [];
                if (item.RejectionReason) {
                    if (typeof item.RejectionReason === 'string' && item.RejectionReason.startsWith('[')) {
                        reasonsArray = JSON.parse(item.RejectionReason);
                    } else {
                        reasonsArray = [item.RejectionReason];
                    }
                } else if (Array.isArray(item.errors)) {
                    reasonsArray = item.errors;
                }
                
                reasons = reasonsArray;

                const reasonToField = {
                    "Project already exists": "ProjectName",
                    "Account Manager not found": "Manager",
                    "Requirement set not found": "RequirementSets",
                    "Duplicate in file": ""
                };

                const resolveCellLetter = (reason) => {
                    const normalizedReason = String(reason || '').trim();
                    if (!normalizedReason) return '';

                    const fieldKey = reasonToField[normalizedReason];
                    if (fieldKey) {
                        return getLetterByKey(fieldKey);
                    }

                    const match = normalizedReason.match(/^(.+?)\s+(is required|has invalid format)\.?$/i);
                    if (match) {
                        return getLetterByLabel(match[1]);
                    }

                    // Custom field rejections: "<Field Name>: <value> is not
                    // recognized" and "<Field Name>: not configured for Holder <id>".
                    const customMatch = normalizedReason.match(/^(.+?):\s*.+$/);
                    if (customMatch) {
                        return getLetterByLabel(customMatch[1]);
                    }

                    return '';
                };

                reasons.forEach(r => {
                    const letter = resolveCellLetter(r);
                    if (letter) {
                        invalidCells.push(letter);
                    }
                });
            } catch {
                reasons = [item.RejectionReason || 'Unknown'];
            }

            const cellStr = [...new Set(invalidCells)].join(',');
            const reasonStr = (Array.isArray(reasons) ? reasons : [reasons]).join(', ');

            const row = [rowNum || ''];
            row.push(`"${cellStr}"`, `"${reasonStr.replace(/"/g, '""')}"`);
            row.push(
                `"${this.escapeCsvValue(item.HolderId)}"`,
                `"${this.escapeCsvValue(item.ProjectName)}"`,
                `"${this.escapeCsvValue(item.CFProjectName2)}"`,
                `"${this.escapeCsvValue(item.Manager)}"`,
                `"${this.escapeCsvValue(item.Address1)}"`,
                `"${this.escapeCsvValue(item.Address2)}"`,
                `"${this.escapeCsvValue(item.City)}"`,
                `"${this.escapeCsvValue(item.State)}"`,
                `"${this.escapeCsvValue(item.CountryId)}"`,
                `"${this.escapeCsvValue(item.ZipCode)}"`,
                `"${this.escapeCsvValue(item.RequirementSets)}"`,
                `"${this.escapeCsvValue(item.CFNote)}"`,
                `"${this.escapeCsvValue(item.AIWording)}"`,
                `"${this.escapeCsvValue(item.CFContactName)}"`,
                `"${this.escapeCsvValue(item.CFContactPhone)}"`,
                `"${this.escapeCsvValue(item.CFProjectEmail)}"`,
                `"${this.escapeCsvValue(item.ProjectNumber)}"`,
                `"${this.escapeCsvValue(item.Description)}"`
            );
            lines.push(row.join(','));
        });

        this.downloadCSV(lines, `project_error_log_${new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)}.csv`);
    }

    // Project-insureds success
    downloadProjectInsuredsSuccessCSV(rows = []) {
        const lines = [
            "Project Insured Id,Holder Id,Insured Name,Project Name,Requirement Set ID,Link"
        ];

        rows.forEach(item => {
            const projectInsuredId = item.newProjectInsuredId || '';
            const link = projectInsuredId ? `${process.env.REACT_APP_FRONTEND_URL}/certfocus/certificates/${projectInsuredId}` : '';
            const row = [
                `"${projectInsuredId}"`,
                `"${this.escapeCsvValue(item.HolderId)}"`,
                `"${this.escapeCsvValue(item.InsuredName)}"`,
                `"${this.escapeCsvValue(item.ProjectName)}"`,
                `"${this.escapeCsvValue(item.RequirementSetId)}"`,
                `"${link}"`
            ];
            lines.push(row.join(','));
        });

        this.downloadCSV(lines, `project_insured_success_log_${new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)}.csv`);
    }

    // Project-insureds error
    downloadProjectInsuredsErrorCSV(rows = [], uploadedHeaders = this.state.uploadedHeaders) {
        const lines = [
            "Row(Reference to Previous File),Cells,Reasons,Holder ID,Insured ID,Project ID,Requirement Set ID"
        ];

        const headerLetterByNormalized = {};
        (uploadedHeaders || []).forEach((header, idx) => {
            const letter = String.fromCharCode(65 + 3 + idx); // P onwards
            const norm = normalizeHeader(header);

            if (norm) headerLetterByNormalized[norm] = letter;
        });
        
        const consolidatedErrors = this.consolidateErrors();
        const errorRows = consolidatedErrors.length > 0 ? consolidatedErrors : rows;

        const templateFields = dataImportConfig.getTemplateFieldDefinitions(3);
        const labelByKey = templateFields.reduce((acc, field) => {
            acc[field.key] = field.label;
            return acc;
        }, {});

        const getLetterByLabel = (label) => {
            if (!label) return '';
            return headerLetterByNormalized[normalizeHeader(label)] || '';
        };

        const getLetterByKey = (key) => getLetterByLabel(labelByKey[key] || key);

        errorRows.forEach(item => {
            let reasons = [];
            let invalidCells = [];

            const rowNum = item.RowNum || item.rowNumber;

            try {
                let reasonsArray = [];
                if (item.RejectionReason) {
                    if (typeof item.RejectionReason === 'string' && item.RejectionReason.startsWith('[')) {
                        reasonsArray = JSON.parse(item.RejectionReason);
                    } else {
                        reasonsArray = [item.RejectionReason];
                    }
                } else if (Array.isArray(item.errors)) {
                    reasonsArray = item.errors;
                }
                
                reasons = reasonsArray;

                const reasonToField = {
                    "Project not found": "ProjectId",
                    "Insured not found": "InsuredId",
                    "Requirement set not found": "RequirementSetId",
                    "Holder not found": "HolderId",
                    "Project insured already exists": "",
                    "Duplicate in file": ""
                };

                const resolveCellLetter = (reason) => {
                    const normalizedReason = String(reason || '').trim();
                    if (!normalizedReason) return '';

                    const fieldKey = reasonToField[normalizedReason];
                    if (fieldKey) {
                        return getLetterByKey(fieldKey);
                    }

                    const match = normalizedReason.match(/^(.+?)\s+(is required|has invalid format)\.?$/i);
                    if (match) {
                        return getLetterByLabel(match[1]);
                    }

                    return '';
                };

                reasons.forEach(r => {
                    const letter = resolveCellLetter(r);
                    if (letter) {
                        invalidCells.push(letter);
                    }
                });
            } catch {
                reasons = [item.RejectionReason || 'Unknown'];
            }

            const cellStr = [...new Set(invalidCells)].join(',');
            const reasonStr = (Array.isArray(reasons) ? reasons : [reasons]).join(', ');

            const row = [rowNum || ''];
            row.push(`"${cellStr}"`, `"${reasonStr.replace(/"/g, '""')}"`);
            row.push(
                `"${this.escapeCsvValue(item.HolderId)}"`,
                `"${this.escapeCsvValue(item.InsuredId)}"`,
                `"${this.escapeCsvValue(item.ProjectId)}"`,
                `"${this.escapeCsvValue(item.RequirementSetId)}"`
            );
            lines.push(row.join(','));
        });

        this.downloadCSV(lines, `project_insured_error_log_${new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)}.csv`);
    }

    // Single Step PI Import - Success CSV
    downloadSingleStepPiSuccessCSV(rows = []) {
        const lines = [
            "Holder ID,Project ID,Insured ID,Project-Insured ID,Insured Name,Contact Name,Contact Phone,Contact Email,Insured URL,Project URL"
        ];

        rows.forEach(item => {
            const projectId = item.newProjectId || item.ProjectId || '';
            const insuredId = item.newInsuredId || item.newInsuredID || '';
            const projectInsuredId = item.newProjectInsuredId || '';

            const insuredUrl = insuredId
                ? `${process.env.REACT_APP_FRONTEND_URL}/certfocus/insureds/${insuredId}`
                : '';

            const projectUrl = projectId
                ? `${process.env.REACT_APP_FRONTEND_URL}/certfocus/projects/${projectId}`
                : '';

            const row = [
                `"${this.escapeCsvValue(item.HolderId)}"`,
                `"${projectId}"`,
                `"${insuredId}"`,
                `"${projectInsuredId}"`,
                `"${this.escapeCsvValue(item.InsuredName)}"`,
                `"${this.escapeCsvValue(item.InsuredContactName || item.ContactName || '')}"`,
                `"${this.escapeCsvValue(item.InsuredContactPhone || item.ContactPhone || '')}"`,
                `"${this.escapeCsvValue(item.InsuredEmail || item.ContactEmail || '')}"`,
                `"${insuredUrl}"`,
                `"${projectUrl}"`
            ];

            lines.push(row.join(','));
        });

        this.downloadCSV(
            lines,
            `single_step_pi_success_${new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)}.csv`
        );
    }

    // Single Step PI Import - Error CSV
    downloadSingleStepPiErrorCSV(rows = [], uploadedHeaders = this.state.uploadedHeaders) {
        const lines = [
            "Row(Reference to Previous File),Step Failed,Reasons,Cells,Holder ID,Insured Name,Project Name,Requirement Set ID,Account Manager,Address,Address Line 2,City,State,Country,Postal Code,Project Contact Name,Project Contact Phone,Project Email Address,Insured Contact Name,Insured Contact Phone,Insured Contact Email,Tax ID,Vendor Number"
        ];

        const headerLetterByNormalized = {};
        (uploadedHeaders || []).forEach((header, idx) => {
            const letter = String.fromCharCode(65 + 4 + idx); // after Row, Step Failed, Reasons, Cells
            const norm = normalizeHeader(header);
            if (norm) headerLetterByNormalized[norm] = letter;
        });

        const consolidatedErrors = this.consolidateErrors ? this.consolidateErrors() : [];
        let errorRows = consolidatedErrors.length > 0 ? consolidatedErrors : rows;

        // de-dupe + sort
        const seen = new Set();
        errorRows = errorRows
            .filter(item => {
                const key = String(item.RowNum || item.rowNumber || '');
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .sort((a, b) => (a.RowNum || a.rowNumber || 0) - (b.RowNum || b.rowNumber || 0));

        const templateFields = dataImportConfig.getTemplateFieldDefinitions(4, this.props.customFields || this.state.customFields || []);
        const labelByKey = templateFields.reduce((acc, field) => {
            acc[field.key] = field.label;
            return acc;
        }, {});

        const getLetterByLabel = (label) => {
            if (!label) return '';
            return headerLetterByNormalized[normalizeHeader(label)] || '';
        };

        const getLetterByKey = (key) => getLetterByLabel(labelByKey[key] || key);

        // Map rejection reasons → which step failed + which column letter
        const resolveStepAndCell = (reason) => {
            const r = String(reason || '').toLowerCase();

            // Project-related
            if (r.includes('project already exists') ||
                r.includes('duplicate project name') ||
                r.includes('account manager not found') ||
                r.includes('requirement set not found') && !r.includes('insured')) {
                return {
                    step: 'Project Creation',
                    letter: r.includes('account manager') ? getLetterByKey('Manager')
                        : r.includes('requirement set') ? getLetterByKey('RequirementSetId')
                        : getLetterByKey('ProjectName')
                };
            }

            // Insured-related
            if (r.includes('insured name missing') ||
                r.includes('email missing') ||
                r.includes('duplicate tax') ||
                r.includes('duplicate vendor') ||
                r.includes('duplicate insured name') ||
                r.includes('country does not match') ||
                r.includes('state does not match') ||
                r.includes('holder not found')) {
                return {
                    step: 'Insured Creation',
                    letter: r.includes('tax') ? getLetterByKey('TaxID')
                        : r.includes('vendor') ? getLetterByKey('VendorNumber')
                        : r.includes('email') ? getLetterByKey('InsuredEmail')
                        : r.includes('insured name') ? getLetterByKey('InsuredName')
                        : r.includes('country') ? getLetterByKey('CountryId')
                        : r.includes('state') ? getLetterByKey('State')
                        : getLetterByKey('HolderId')
                };
            }

            // Project-Insured connection related
            if (r.includes('project insured already exists') ||
                r.includes('project not found') ||
                r.includes('insured not found')) {
                return {
                    step: 'Project-Insured Connection Creation',
                    letter: ''
                };
            }

            // Fallback
            return { step: 'Unknown', letter: '' };
        };

        errorRows.forEach(item => {
            let reasons = [];
            try {
                if (item.RejectionReason) {
                    reasons = typeof item.RejectionReason === 'string' && item.RejectionReason.startsWith('[')
                        ? JSON.parse(item.RejectionReason)
                        : [item.RejectionReason];
                } else if (Array.isArray(item.errors)) {
                    reasons = item.errors;
                } else {
                    reasons = ['Unknown error'];
                }
            } catch {
                reasons = [item.RejectionReason || 'Unknown error'];
            }

            // Determine primary step (first reason wins)
            const primary = resolveStepAndCell(reasons[0]);
            const stepFailed = primary.step;

            // Collect all cell letters
            const cells = [];
            reasons.forEach(r => {
                const resolved = resolveStepAndCell(r);
                if (resolved.letter) cells.push(resolved.letter);
            });

            const cellStr = [...new Set(cells)].sort().join(',');
            const reasonStr = reasons.join(', ');

            const row = [
                `"${item.RowNum || item.rowNumber || ''}"`,
                `"${stepFailed}"`,
                `"${reasonStr.replace(/"/g, '""')}"`,
                `"${cellStr}"`,
                `"${this.escapeCsvValue(item.HolderId)}"`,
                `"${this.escapeCsvValue(item.InsuredName)}"`,
                `"${this.escapeCsvValue(item.ProjectName)}"`,
                `"${this.escapeCsvValue(item.RequirementSetId || item.RequirementSets)}"`,
                `"${this.escapeCsvValue(item.Manager)}"`,
                `"${this.escapeCsvValue(item.Address1 || item.Address)}"`,
                `"${this.escapeCsvValue(item.Address2 || item['Address Line 2'])}"`,
                `"${this.escapeCsvValue(item.City)}"`,
                `"${this.escapeCsvValue(item.State)}"`,
                `"${this.escapeCsvValue(item.CountryId || item.Country)}"`,
                `"${this.escapeCsvValue(item.ZipCode || item.PostalCode)}"`,
                `"${this.escapeCsvValue(item.CFContactName)}"`,
                `"${this.escapeCsvValue(item.CFContactPhone)}"`,
                `"${this.escapeCsvValue(item.CFProjectEmail)}"`,
                `"${this.escapeCsvValue(item.InsuredContactName)}"`,
                `"${this.escapeCsvValue(item.InsuredContactPhone)}"`,
                `"${this.escapeCsvValue(item.InsuredEmail)}"`,
                `"${this.escapeCsvValue(item.TaxId || item.TaxID)}"`,
                `"${this.escapeCsvValue(item.VendorNumber)}"`
            ];

            lines.push(row.join(','));
        });

        this.downloadCSV(
            lines,
            `single_step_pi_error_${new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)}.csv`
        );
    }

    // Common error header + row number
    getErrorBaseRow(item) {
        return [item.RowNum || ''];
    }

    getImportTypeDisplayName = (typeName) => {
        if (!typeName) return '';
        if (typeName === 'Single-Step PI Import Template') {
            return 'Single-Step Import';
        }
        return typeName.replace(' Import Template', '');
    };

    render() {
        const { templateTypes, onClose, tabs } = this.props;
        const { title, selectPlaceholder, uploadErrorsTitle, fileUploadBtnText, fileUploadLabel, cancelBtnText, importBtnText, uploadWarningsTitle } = tabs.dataImports.importModal;
        const {
            selected,
            selectedFile,
            isImporting,
            fileErrors,
            fileHeaderErrors,
            isDragging,
            validationErrors,
            failedRows,
            validRows,
            warningRows,
            warningsCount
        } = this.state;

        const hasFileErrors = fileErrors.length > 0 || fileHeaderErrors.length > 0;
        const hasValidationFailure = hasFileErrors || validRows.length < 1;
        const showRunImportButton = !!selected && !!selectedFile && !hasValidationFailure;
        const canImport = showRunImportButton && !isImporting && !this.state.isLoadingFile;

        return (
            <div className="add-entity-form-small import-data-modal-container">
                <div className="add-item-header">
                    <h1>{title}</h1>
                </div>

                <section className="white-section">
                    <div className="add-item-form-subsection">
                        <div className="form-group mb-4">
                            <select
                                className={`form-control ${!selected ? 'is-placeholder' : ''}`}
                                value={selected}
                                onChange={this.handleTypeChange}
                                disabled={selected && (isImporting || !!selectedFile)}
                                placeholder={selectPlaceholder}
                            >
                                <option className='is-placeholder' value="">{selectPlaceholder}</option>
                                {templateTypes.map(type => (
                                    <option key={type.id} value={type.id}>
                                        {this.getImportTypeDisplayName(type.name)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div
                            className={`form-group upload-container ${isDragging ? 'drag-over' : ''}`}
                            onDragOver={e => e.preventDefault()}
                            onDragEnter={e => {
                                e.preventDefault();
                                this.setState({ isDragging: true });
                            }}
                            onDragLeave={e => {
                                e.preventDefault();
                                this.setState({ isDragging: false });
                            }}
                            onDrop={e => {
                                e.preventDefault();
                                this.setState({ isDragging: false });
                                const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
                                if (file) this.handleFileChange({ dataTransfer: e.dataTransfer });
                            }}
                        >
                            <label>{fileUploadLabel}</label>
                            <div className='or'>
                                <div className='hr-line' />
                                <span>OR</span>
                                <div className='hr-line' />
                            </div>
                            <button
                                className="btn btn-file-upload"
                                onClick={() => {
                                    document.getElementById('hiddenFileInput').value = '';
                                    document.getElementById('hiddenFileInput').click()
                                }}
                                disabled={isImporting || this.state.isLoadingFile}
                            >
                                {fileUploadBtnText}
                            </button>
                            <input
                                id="hiddenFileInput"
                                type="file"
                                accept=".csv"
                                onChange={this.handleFileChange}
                                style={{ display: 'none' }}
                                disabled={isImporting}
                            />
                        </div>
                        {this.state.isLoadingFile && (<div className='fileUploadLoader'><SpinnerLoader /></div>)}
                        {!this.state.isLoadingFile && selectedFile && (
                            <div className="uploaded-file-info">
                                <strong>{selectedFile.name}</strong>
                                <span className="text-muted ms-2">
                                    {selectedFile.size >= 1024 * 1024
                                        ? `(${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)`
                                        : `(${(selectedFile.size / 1024).toFixed(1)} KB)`}
                                </span>
                                <button
                                    className="btn btn-remove-file"
                                    disabled={isImporting}
                                    onClick={() => this.setState({
                                            selectedFile: null,
                                            fileErrors: [],
                                            fileHeaderErrors: [],
                                            validationErrors: [],
                                            warningRows: [],
                                            warningsCount: 0,
                                            uploadedHeaders: [],
                                            rawRows: [],
                                            validRows: [],
                                            failedRows: [],
                                            importLogs: []
                                        })}
                                >
                                    Remove
                                </button>
                            </div>
                        )}

                        {!this.state.isLoadingFile && (fileErrors.length > 0 || fileHeaderErrors.length > 0 || validationErrors.length > 0 || warningsCount > 0) && (
                            <>
                                {(fileErrors.length > 0 || fileHeaderErrors.length > 0 || validationErrors.length > 0) && (
                                    <div className="error-container">
                                        <span className='error-title'>{uploadErrorsTitle}</span>
                                        <div className="errors">
                                            {fileErrors.map((err, i) => <div key={i}>- {err}</div>)}
                                            {fileHeaderErrors.map((err, i) => <div key={i}>- {err}</div>)}
                                            {validationErrors.map((err, i) => <div key={i}>- {err}.</div>)}
                                        </div>
                                    </div>
                                )}

                                {warningsCount > 0 && (
                                    <div className="warning-container">
                                        <span className="warning-title">{uploadWarningsTitle}</span>
                                        <div className="warnings">
                                            {warningRows.slice(0, 4).map((w, i) => (
                                                <div key={i}>- Row {w.rowNumber}: {w.warnings.join(', ')}.</div>
                                            ))}
                                            {warningsCount > 4 && <div>... and {warningsCount - 4} more</div>}
                                        </div>
                                    </div>
                                )}
                                <div className="note">
                                    {(fileErrors.length > 0 || fileHeaderErrors.length > 0 || validationErrors.length > 0 || warningsCount > 0) && (<strong>Note: </strong>)}
                                    {
                                        (fileErrors.length > 0 || fileHeaderErrors.length > 0) && (
                                            <div>- Please re-attach the file without errors to enable the <strong>Import</strong> button.</div>            
                                        )
                                    }
                                    {
                                        validationErrors.length > 0 && fileErrors.length === 0 && (
                                            <div>- <strong>Row validation errors</strong> detected. Rows with errors will be excluded from import. Valid rows will be imported.</div>         
                                        )
                                    }
                                    {
                                        warningsCount > 0 && (
                                            <div>- <strong>Warnings</strong> are informational only. <strong>Import</strong> can proceed.</div>         
                                        )
                                    }
                                </div>
                            </>
                        )}
                        <div className="add-item-bn text-right">
                            <button
                                className="bn bn-small cancel-add-item mr-2"
                                onClick={onClose}
                                disabled={isImporting}
                            >
                                {cancelBtnText}
                            </button>
                            {showRunImportButton && (
                                <button
                                    className="bn bn-small bg-green-dark-gradient create-item-bn"
                                    onClick={this.handleImport}
                                    disabled={!canImport}
                                >
                                    {isImporting ? 'Importing...' : (importBtnText || 'Run Import')}
                                </button>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        );
    }

}

const mapStateToProps = (state) => {
    return {
        currentUserName: state.login.profile.FirstName.concat(" ", state.login.profile.LastName),
        tabs: state.localization.strings.holders.holdersProfile.tabs,
        holderID: state.holdersProfile.profileData.id || null,
        holderChildrens: state.holders.holderChildren || [],
        reduxCustomFields: state.dataImports && state.dataImports.customFields ? state.dataImports.customFields : []
    }
};

const mapDispatchToProps = (dispatch) => ({
    reloadImportHistory: (holderId) => {
        dispatch(fetchPreviousImports(holderId));
    },
    // Bound explicitly because connect() only injects a raw `dispatch` prop when
    // mapDispatchToProps is omitted. storeInRedux is false so a lookup for a
    // child Holder never overwrites the page Holder's custom fields.
    loadHolderCustomFields: (holderId) => dispatch(fetchCustomFieldsForImport(holderId, false))
});

export default connect(mapStateToProps, mapDispatchToProps)(ImportDataModal);
